import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  PencilSimple,
  Eye,
  EyeSlash,
  SpinnerGap,
  Clock,
  CalendarBlank,
  NoteBlank,
  FloppyDisk,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCED_FROM_LABELS = {
  linkedin: 'LinkedIn',
  career_builder: 'Career Builder',
  monster: 'Monster',
  indeed: 'Indeed',
  reference: 'Reference',
  existing_employee: 'Existing Employee',
};

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'INR') return `₹${amount?.toLocaleString('en-IN') || 0}`;
  return `$${amount?.toLocaleString('en-US') || 0}`;
};

const SALES_CATEGORIES = [
  { value: 'new_cpt_opt', label: 'New CPT/OPT' },
  { value: 'sales_recruiting_direct', label: 'Sales Recruiting Direct' },
  { value: 'existing_cpt_opt', label: 'Existing CPT/OPT' },
  { value: 'any_new_h1t', label: 'Any New H1T' },
  { value: 'existing_h1_gc_usc', label: 'Existing H1/GC/USC' },
  { value: 'low_end_existing', label: 'Low End Existing' },
  { value: 'low_end_new', label: 'Low End New' },
];

const RECRUITING_CATEGORIES = [
  { value: 'existing_bench', label: 'Existing Bench' },
  { value: 'new_bench', label: 'New Bench' },
  { value: 'core_recruiting', label: 'Core Recruiting' },
  { value: 'c2c', label: 'C2C' },
  { value: 'internal_candidate', label: 'Internal Candidate' },
  { value: 'fulltime', label: 'Full-time' },
];

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700',
    submitted: 'bg-blue-100 text-blue-700',
    reviewing: 'bg-amber-100 text-amber-700',
    pending_completion: 'bg-purple-100 text-purple-700',
    pending_lead_review: 'bg-orange-100 text-orange-700',
    lead_approved: 'bg-teal-100 text-teal-700',
    pending_manager_review: 'bg-indigo-100 text-indigo-700',
    manager_approved: 'bg-blue-100 text-blue-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    backout: 'bg-rose-100 text-rose-700',
    backed_out: 'bg-rose-100 text-rose-700',
  };
  const labels = {
    draft: 'DRAFT',
    submitted: 'SUBMITTED',
    reviewing: 'REVIEWING',
    pending_completion: 'AWAITING COMPLETION',
    pending_lead_review: 'PENDING REVIEW',
    lead_approved: 'PENDING MANAGER',
    pending_manager_review: 'PENDING MANAGER REVIEW',
    manager_approved: 'PENDING SA',
    approved: 'INCENTIVE CONFIRMED',
    rejected: 'REJECTED',
    backout: 'BACKOUT',
    backed_out: 'BACKED OUT',
  };
  return <Badge className={`${styles[status] || 'bg-slate-100 text-slate-700'} text-sm px-3 py-1`}>{labels[status] || status?.toUpperCase()}</Badge>;
};

const PlacementDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isManager, isLead, isSuperadmin, isRecruiter } = useAuth();
  const [placement, setPlacement] = useState(null);
  const [entityVendors, setEntityVendors] = useState([]);
  const [entityClients, setEntityClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showLeadApproveModal, setShowLeadApproveModal] = useState(false);
  const [showManagerApproveModal, setShowManagerApproveModal] = useState(false);
  const [showBackoutModal, setShowBackoutModal] = useState(false);
  const [backoutNote, setBackoutNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const [sixtyDayStatus, setSixtyDayStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAdminIncentiveModal, setShowAdminIncentiveModal] = useState(false);
  const [adminIncentiveForm, setAdminIncentiveForm] = useState({ amount: '', reason: '' });
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [deductionForm, setDeductionForm] = useState({ amount: '', reason: '' });
  const [showRecruiterBackoutModal, setShowRecruiterBackoutModal] = useState(false);
  const [recruiterBackoutReason, setRecruiterBackoutReason] = useState('');
  const [isEditingSplits, setIsEditingSplits] = useState(false);
  const [showRateInfoPopup, setShowRateInfoPopup] = useState(false);
  const [splitEdits, setSplitEdits] = useState({});
  const [savingSplits, setSavingSplits] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [editingCustom, setEditingCustom] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);

  const [completionForm, setCompletionForm] = useState({ completion_notes: '', end_date: '' });
  const [leadForm, setLeadForm] = useState({ lead_notes: '', recruiter_amount: '', lead_amount: '', manager_amount: '' });
  const [managerForm, setManagerForm] = useState({ manager_notes: '', recruiter_amount: '', lead_amount: '', manager_amount: '' });

  const [overrideData, setOverrideData] = useState({
    sales_recruiter_amount: '', sourcing_recruiter_amount: '', sales_lead_amount: '',
    sales_manager_amount: '', sourcing_lead_amount: '', sourcing_manager_amount: '', override_reason: '',
  });

  const isRecruitingModule = placement?.module === 'recruiting';
  const isSalesModule = placement?.module === 'sales';
  const isRecruitingLead = user?.role === 'recruiting_lead';
  const isRecruitingManager = user?.role === 'recruiting_manager';
  const isSalesLead = user?.role === 'sales_lead';
  const isSalesManager = user?.role === 'sales_manager';
  const isCeo = user?.role === 'ceo';
  const isAdmin = isSuperadmin || isCeo;
  
  // Module-matched checks
  const isModuleManager = (isSalesModule && isSalesManager) || (isRecruitingModule && isRecruitingManager);
  const isModuleLead = (isSalesModule && isSalesLead) || (isRecruitingModule && isRecruitingLead);

  // Split / additional partner takes priority over lead/manager share display
  const isSplitRecipient = Boolean(
    placement?.is_shared_split
    || (placement?.split_share?.user_id === user?.id && placement?.created_by !== user?.id)
  );
  const isAdditionalSplitRecipient = Boolean(
    placement?.is_additional_split
    || (placement?.additional_splits || []).some((sp) => sp.user_id === user?.id && placement?.created_by !== user?.id)
  );
  const partnerPayout = placement?.my_projected_payout
    ?? placement?.my_split_amount
    ?? placement?.incentives?.my_split
    ?? 0;

  const fmtSensitive = (amount) => (showSensitiveData ? formatCurrency(amount) : '••••••');
  const toggleSensitiveAmounts = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowSensitiveData((prev) => !prev);
  };

  useEffect(() => { fetchPlacement(); fetchCustomFields(); 
    axios.get(`${API_URL}/api/entities/dropdown?entity_type=vendor`, { withCredentials: true }).then(r => setEntityVendors(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/entities/dropdown?entity_type=client`, { withCredentials: true }).then(r => setEntityClients(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (placement?.module === 'recruiting' && ['submitted', 'lead_approved'].includes(placement?.status)) {
      checkSixtyDayStatus();
    }
  }, [placement]);

  const fetchPlacement = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placements/${id}`, { withCredentials: true });
      setPlacement(response.data);
      const p = response.data;
      // Init edit data
      setEditData({
        candidate_name: p.candidate_name, vendor: p.vendor, client: p.client,
        company: p.company || '', vendor_type: p.vendor_type || 'existing', client_type: p.client_type || 'existing',
        bill_rate: p.bill_rate, pay_rate: p.pay_rate, category: p.category,
        start_date: p.start_date || '', project_end_date: p.project_end_date || '',
        sourcing_support: p.sourcing_support || '',
        is_new_technology: p.is_new_technology || false, new_technology_name: p.new_technology_name || '',
        is_internal_candidate: p.is_internal_candidate || false, is_fulltime: p.is_fulltime || false,
      });
      // Init override/review forms
      if (p.incentives && p.module !== 'recruiting') {
        setOverrideData({
          sales_recruiter_amount: p.overrides?.sales_recruiter_amount || p.incentives.sales_recruiter || '',
          sourcing_recruiter_amount: p.overrides?.sourcing_recruiter_amount || p.incentives.sourcing_recruiter || '',
          sales_lead_amount: p.overrides?.sales_lead_amount || p.incentives.sales_lead || '',
          sales_manager_amount: p.overrides?.sales_manager_amount || p.incentives.sales_manager || p.my_split_amount || '',
          sourcing_lead_amount: p.overrides?.sourcing_lead_amount || p.incentives.sourcing_lead || '',
          sourcing_manager_amount: p.overrides?.sourcing_manager_amount || p.incentives.sourcing_manager || '',
          override_reason: p.overrides?.reason || '',
        });
      }
      if (p.module === 'recruiting' && p.incentives) {
        setLeadForm({
          lead_notes: '',
          recruiter_amount: '',
          lead_amount: p.overrides?.lead_amount || p.incentives.lead || p.my_split_amount || '',
          manager_amount: '',
        });
        setManagerForm({
          manager_notes: '',
          recruiter_amount: '',
          lead_amount: '',
          manager_amount: p.overrides?.manager_amount || p.incentives.manager || p.my_split_amount || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch placement:', error);
      if (error.response?.status === 404 || error.response?.status === 403) navigate('/dashboard/placements');
    } finally { setLoading(false); }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/custom-fields`, { withCredentials: true });
      setCustomFields((res.data || []).filter(f => f.applies_to === 'placement' || f.applies_to === 'both'));
    } catch { /* non-admin */ }
  };

  const checkSixtyDayStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placements/${id}/sixty-day-status`, { withCredentials: true });
      setSixtyDayStatus(response.data);
    } catch (error) { console.error('Failed to check 60-day status:', error); }
  };

  // Save edited placement data
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/placements/${id}`, editData, { withCredentials: true });
      toast.success('Placement updated successfully');
      setIsEditing(false);
      fetchPlacement();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally { setSaving(false); }
  };

  // Initialize split edits from current placement data
  const startEditingSplits = () => {
    const inc = placement.incentives || {};
    const ov = placement.overrides || {};
    const keys = isRecruitingModule
      ? ['recruiter', 'lead', 'manager']
      : ['sales_recruiter', 'sourcing_recruiter', 'sales_lead', 'sales_manager', 'sourcing_lead', 'sourcing_manager'];
    const edits = {};
    keys.forEach(k => {
      const val = ov[`${k}_amount`] !== undefined ? ov[`${k}_amount`] : inc[k];
      if (val !== undefined && val !== null) edits[k] = String(val);
    });
    setSplitEdits(edits);
    setShowSensitiveData(true);
    setIsEditingSplits(true);
  };

  const handleSaveSplits = async () => {
    setSavingSplits(true);
    try {
      const payload = {};
      Object.entries(splitEdits).forEach(([k, v]) => { payload[k] = parseFloat(v) || 0; });
      await axios.put(`${API_URL}/api/placements/${id}/override-splits`, payload, { withCredentials: true });
      toast.success('Incentive splits updated');
      setIsEditingSplits(false);
      fetchPlacement();
    } catch (error) {
      toast.error('Failed to save splits: ' + (error.response?.data?.detail || error.message));
    } finally { setSavingSplits(false); }
  };

  const startEditingCustom = () => {
    setCustomValues(placement?.custom_fields || {});
    setEditingCustom(true);
  };

  const handleSaveCustom = async () => {
    setSavingCustom(true);
    try {
      await axios.put(`${API_URL}/api/admin/custom-field-values/placement/${id}`, { values: customValues }, { withCredentials: true });
      toast.success('Custom fields saved');
      setEditingCustom(false);
      fetchPlacement();
    } catch (e) { toast.error('Failed to save: ' + (e.response?.data?.detail || e.message)); }
    finally { setSavingCustom(false); }
  };

  // Sales workflow actions
  const handleStartReview = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/review`, {}, { withCredentials: true });
      fetchPlacement();
    } catch (error) { toast.error('Failed to start review: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this placement?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/approve`, {}, { withCredentials: true });
      fetchPlacement();
    } catch (error) { toast.error('Failed to approve: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, { withCredentials: true });
      setShowRejectModal(false);
      fetchPlacement();
    } catch (error) { toast.error('Failed to reject: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleOverride = async () => {
    if (!overrideData.override_reason.trim()) { toast.error('Please provide an override reason'); return; }
    setActionLoading(true);
    try {
      const payload = { override_reason: overrideData.override_reason };
      const keys = isAdmin
        ? ['sales_recruiter_amount', 'sourcing_recruiter_amount', 'sales_lead_amount', 'sales_manager_amount', 'sourcing_lead_amount', 'sourcing_manager_amount']
        : ['sales_manager_amount'];
      keys.forEach(k => {
        if (overrideData[k]) payload[k] = parseFloat(overrideData[k]);
      });
      await axios.post(`${API_URL}/api/placements/${id}/override`, payload, { withCredentials: true });
      setShowOverrideModal(false);
      fetchPlacement();
    } catch (error) { toast.error('Failed to override: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  // Recruiting workflow actions
  const handleAddCompletion = async () => {
    if (!completionForm.completion_notes.trim()) { toast.error('Please add completion notes'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/add-completion`, completionForm, { withCredentials: true });
      setShowCompletionModal(false);
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleLeadApprove = async () => {
    if (!leadForm.lead_notes.trim()) { toast.error('Please add lead review notes'); return; }
    setActionLoading(true);
    try {
      // Leads may only adjust their own share — never team-member incentive amounts
      const payload = {
        lead_notes: leadForm.lead_notes,
        recruiter_amount: null,
        lead_amount: leadForm.lead_amount ? parseFloat(leadForm.lead_amount) : null,
        manager_amount: null,
      };
      await axios.post(`${API_URL}/api/placements/${id}/lead-approve`, payload, { withCredentials: true });
      setShowLeadApproveModal(false);
      toast.success('Submitted to Manager for final review');
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleManagerApprove = async () => {
    if (!managerForm.manager_notes.trim()) { toast.error('Please add manager review notes'); return; }
    setActionLoading(true);
    try {
      // Managers may only adjust their own share — never team-member incentive amounts
      const payload = {
        manager_notes: managerForm.manager_notes,
        recruiter_amount: null,
        lead_amount: null,
        manager_amount: managerForm.manager_amount ? parseFloat(managerForm.manager_amount) : null,
      };
      await axios.post(`${API_URL}/api/placements/${id}/manager-approve`, payload, { withCredentials: true });
      setShowManagerApproveModal(false);
      toast.success('Sent to admin for final approval');
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleManagerBackout = async () => {
    if (!backoutNote.trim()) { toast.error('Backout note is mandatory'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/manager-backout`, { backout_note: backoutNote }, { withCredentials: true });
      setShowBackoutModal(false);
      toast.success('Placement backed out');
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleAddAdminIncentive = async () => {
    if (!adminIncentiveForm.reason.trim()) { toast.error('Reason is mandatory'); return; }
    if (!adminIncentiveForm.amount || parseFloat(adminIncentiveForm.amount) <= 0) { toast.error('Amount must be greater than 0'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/admin-incentive`, {
        amount: parseFloat(adminIncentiveForm.amount),
        reason: adminIncentiveForm.reason.trim(),
      }, { withCredentials: true });
      setShowAdminIncentiveModal(false);
      setAdminIncentiveForm({ amount: '', reason: '' });
      toast.success('Additional incentive added');
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleAddDeduction = async () => {
    if (!deductionForm.reason.trim()) { toast.error('Reason is mandatory'); return; }
    if (!deductionForm.amount || parseFloat(deductionForm.amount) <= 0) { toast.error('Amount must be greater than 0'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/admin-deduction`, {
        amount: parseFloat(deductionForm.amount),
        reason: deductionForm.reason.trim(),
      }, { withCredentials: true });
      setShowDeductionModal(false);
      setDeductionForm({ amount: '', reason: '' });
      toast.success('Deduction added');
      fetchPlacement();
    } catch (error) { toast.error('Failed: ' + (error.response?.data?.detail || error.message)); }
    finally { setActionLoading(false); }
  };

  const handleRecruiterBackout = async () => {
    if (!recruiterBackoutReason.trim()) { toast.error('Reason for backout is mandatory'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/placements/${id}/recruiter-backout`, { reason: recruiterBackoutReason.trim() }, { withCredentials: true });
      setShowRecruiterBackoutModal(false);
      toast.success('Placement marked as backed out');
      fetchPlacement();
    } catch (error) { toast.error(error.response?.data?.detail || error.message); }
    finally { setActionLoading(false); }
  };

  // Permissions - module-matched
  const isSelfSubmission = placement?.created_by === user?.id;
  const pendingManagerStatuses = ['submitted', 'reviewing', 'pending_lead', 'pending_lead_review', 'lead_approved', 'pending_manager', 'pending_manager_review'];
  const canLeadStandIn = isModuleLead
    && pendingManagerStatuses.includes(placement?.status)
    && !isSelfSubmission
    && (
      placement?.can_approve === true
      || (placement?.can_approve === undefined && placement?.has_assigned_manager === false)
    );
  const canReview = (isModuleManager || isSuperadmin || isCeo) && [...pendingManagerStatuses, 'manager_approved'].includes(placement?.status);
  const canSaFinalApprove = (isSuperadmin || isCeo)
    && (placement?.can_approve !== undefined
      ? Boolean(placement.can_approve)
      : [...pendingManagerStatuses, 'manager_approved'].includes(placement?.status));
  const canApprove = canSaFinalApprove;
  const canOverride = (isModuleManager || isSuperadmin || isCeo) && !['approved', 'sa_approved', 'rejected', 'backed_out'].includes(placement?.status);
  const canAddCompletion = isRecruitingModule && placement?.created_by === user?.id
    && ['submitted', 'pending_manager_review', 'pending_lead_review', 'lead_approved'].includes(placement?.status);
  const canLeadApprove = false;
  const canManagerApprove = isModuleManager && Boolean(placement?.can_approve)
    && pendingManagerStatuses.includes(placement?.status);
  const canManagerBackout = isRecruitingModule && (isRecruitingManager || isSuperadmin || isCeo)
    && ['pending_manager_review', 'lead_approved', 'pending_manager'].includes(placement?.status);
  const managerSixtyDayBlocked = canManagerApprove && sixtyDayStatus && !sixtyDayStatus.can_manager_act && !isSuperadmin;
  const canRejectRecruiting = isRecruitingModule && (isRecruitingLead || isSuperadmin) && ['pending_lead_review'].includes(placement?.status);
  const showSalesReviewActions = canReview || (canLeadStandIn && !isRecruitingModule);
  
  // Edit permissions - module-matched: leads and managers can edit all fields
  const canLeadEdit = false; // Leads cannot edit placements
  const canManagerEdit = false; // Managers cannot edit placements
  const canEdit = (isSuperadmin && !['approved', 'rejected', 'backout', 'backed_out'].includes(placement?.status));

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;
  if (!placement) return <div className="text-center py-12"><p className="text-slate-500">Placement not found</p><Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/placements')}>Back to Placements</Button></div>;

  const categories = isRecruitingModule ? RECRUITING_CATEGORIES : SALES_CATEGORIES;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="placement-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">{placement.candidate_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isRecruitingModule ? 'bg-teal-100 text-teal-800' : 'bg-blue-100 text-blue-800'}`}>
                {isRecruitingModule ? 'Recruiting' : 'Sales'}
              </span>
              <p className="text-slate-500 text-sm">{placement.category?.replace(/_/g, ' ').toUpperCase()}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && !isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-placement-btn">
              <PencilSimple size={16} className="mr-2" /> Edit
            </Button>
          )}
          {getStatusBadge(placement.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">
                {isEditing ? 'Edit Placement Details' : 'Placement Details'}
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                {isEditing ? 'Update the placement fields below. Changes are saved when you click Save.' : 'Core information about this placement including candidate, rates, dates, and vendor details.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                /* Editable form */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Candidate Name</Label>
                      <Input value={editData.candidate_name} onChange={(e) => setEditData({...editData, candidate_name: e.target.value})} data-testid="edit-candidate-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={editData.category} onValueChange={(v) => setEditData({...editData, category: v})}>
                        <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Organization</Label>
                      <Input value={editData.company || ''} onChange={(e) => setEditData({...editData, company: e.target.value})} data-testid="edit-company" />
                    </div>
                    <div className="space-y-2">
                      <Label>{isRecruitingModule ? 'MSP' : 'Vendor'}</Label>
                      {isRecruitingModule ? (
                        <Select value={editData.vendor} onValueChange={(v) => setEditData({...editData, vendor: v})}>
                          <SelectTrigger data-testid="edit-vendor"><SelectValue placeholder="Select MSP" /></SelectTrigger>
                          <SelectContent>
                            {entityVendors.map(v => (
                              <SelectItem key={v} value={v}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={editData.vendor} onChange={(e) => setEditData({...editData, vendor: e.target.value})} data-testid="edit-vendor" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{isRecruitingModule ? 'Client' : 'Vendor Type'}</Label>
                      {isRecruitingModule ? (
                        <Select value={editData.client || ''} onValueChange={(v) => setEditData({...editData, client: v})}>
                          <SelectTrigger data-testid="edit-type"><SelectValue placeholder="Select Client" /></SelectTrigger>
                          <SelectContent>
                            {entityClients.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Select value={editData.vendor_type || 'existing'} onValueChange={(v) => setEditData({...editData, vendor_type: v})}>
                          <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New Vendor</SelectItem>
                            <SelectItem value="existing_new">Existing New Vendor</SelectItem>
                            <SelectItem value="new_client">New Client</SelectItem>
                            <SelectItem value="old">Old Vendor</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>End Client</Label>
                      <Input value={editData.client} onChange={(e) => setEditData({...editData, client: e.target.value})} data-testid="edit-client" />
                    </div>
                    <div className="space-y-2">
                      <Label>Bill Rate ($/hr)</Label>
                      <Input type="number" step="0.01" value={editData.bill_rate} onChange={(e) => setEditData({...editData, bill_rate: parseFloat(e.target.value) || 0})} data-testid="edit-bill-rate" />
                    </div>
                    <div className="space-y-2">
                      <Label>Pay Rate ($/hr)</Label>
                      <Input type="number" step="0.01" value={editData.pay_rate} onChange={(e) => setEditData({...editData, pay_rate: parseFloat(e.target.value) || 0})} data-testid="edit-pay-rate" />
                    </div>
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input type="date" value={editData.start_date || ''} onChange={(e) => setEditData({...editData, start_date: e.target.value})} data-testid="edit-start-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>Project End Date</Label>
                      <Input type="date" value={editData.project_end_date || ''} onChange={(e) => setEditData({...editData, project_end_date: e.target.value})} data-testid="edit-project-end-date" />
                    </div>
                  </div>
                  {editData.bill_rate && editData.pay_rate && (() => {
                    const discPct = placement?.discount_info?.discount_pct || 0;
                    const fdr = editData.bill_rate - (editData.bill_rate * discPct / 100);
                    const margin = fdr - editData.pay_rate;
                    return (
                      <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                        {discPct > 0 ? (
                          <>Calculated Margin: <strong>${margin.toFixed(2)}/hr</strong> <span className="text-xs text-amber-600 ml-2">(FDR ${fdr.toFixed(2)} after {discPct}% discount)</span></>
                        ) : (
                          <>Calculated Margin: <strong>${margin.toFixed(2)}/hr</strong></>
                        )}
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 pt-2">
                    <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                      {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                /* Read-only view */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {placement.company && <div><p className="text-sm text-slate-500">Organization</p><p className="font-medium">{placement.company}</p></div>}
                    <div><p className="text-sm text-slate-500">{isRecruitingModule ? 'MSP' : 'Vendor'}</p><p className="font-medium">{placement.vendor} {!isRecruitingModule && <span className={`text-xs px-1.5 py-0.5 rounded ${placement.vendor_type === 'new' ? 'bg-emerald-100 text-emerald-700' : placement.vendor_type === 'old' ? 'bg-red-100 text-red-700' : placement.vendor_type === 'existing_new' ? 'bg-cyan-100 text-cyan-700' : placement.vendor_type === 'new_client' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{placement.vendor_type === 'new' ? 'New Vendor' : placement.vendor_type === 'old' ? 'Old Vendor' : placement.vendor_type === 'existing_new' ? 'Existing New Vendor' : placement.vendor_type === 'new_client' ? 'New Client' : 'Old Vendor'}</span>}</p></div>
                    <div><p className="text-sm text-slate-500">{isRecruitingModule ? 'Client' : 'End Client'}</p><p className="font-medium">{placement.client} {!isRecruitingModule && placement.client_type && <span className={`text-xs px-1.5 py-0.5 rounded ${placement.client_type === 'new' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{placement.client_type === 'new' ? 'New Client' : 'Existing Client'}</span>}</p></div>
                    <div><p className="text-sm text-slate-500">Category</p><p className="font-medium">{placement.effective_category?.replace(/_/g, ' ')}</p></div>
                    {placement.sourced_from && placement.sourced_from !== 'None' && (
                      <div data-testid="sourced-from-display"><p className="text-sm text-slate-500">Sourced From</p><p className="font-medium"><span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                        placement.sourced_from === 'linkedin' ? 'bg-blue-100 text-blue-700' :
                        placement.sourced_from === 'career_builder' ? 'bg-purple-100 text-purple-700' :
                        placement.sourced_from === 'monster' ? 'bg-emerald-100 text-emerald-700' :
                        placement.sourced_from === 'indeed' ? 'bg-amber-100 text-amber-700' :
                        placement.sourced_from === 'existing_employee' ? 'bg-teal-100 text-teal-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>{SOURCED_FROM_LABELS[placement.sourced_from] || placement.sourced_from}</span></p></div>
                    )}
                    {placement.employment_type && (
                      <div data-testid="employment-type-display"><p className="text-sm text-slate-500">Employment Type</p><p className="font-medium"><span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                        placement.employment_type === 'w2' ? 'bg-blue-100 text-blue-700' :
                        placement.employment_type === 'c2c' ? 'bg-purple-100 text-purple-700' :
                        placement.employment_type === 'c2c_self' ? 'bg-indigo-100 text-indigo-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>{placement.employment_type === 'c2c_self' ? 'C2C Self' : placement.employment_type.toUpperCase()}</span>
                      {placement.employment_type === 'c2c' && placement.c2c_company && <span className="ml-2 text-sm text-slate-600">via {placement.c2c_company}</span>}
                      </p></div>
                    )}
                  </div>

                  {/* Rate Information with Candidate % / Company % */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="rate-info-section">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Rate Information</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-2 bg-white rounded border border-slate-200">
                        <p className="text-xs text-slate-500">Bill Rate</p>
                        <p className="text-base font-bold text-slate-900">${placement.bill_rate}/hr</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-slate-200">
                        <p className="text-xs text-slate-500">Pay Rate</p>
                        <p className="text-base font-bold text-slate-900">${placement.pay_rate}/hr</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-slate-200">
                        <p className="text-xs text-slate-500">Candidate %</p>
                        <p className="text-base font-bold text-blue-700" data-testid="detail-candidate-pct">
                          {placement.candidate_percentage ? `${placement.candidate_percentage}%` : placement.bill_rate > 0 ? `${((placement.pay_rate / placement.bill_rate) * 100).toFixed(1)}%` : '-'}
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-slate-200">
                        <p className="text-xs text-slate-500">Company %</p>
                        <p className="text-base font-bold text-emerald-700" data-testid="detail-company-pct">
                          {placement.company_percentage ? `${placement.company_percentage}%` : placement.bill_rate > 0 ? `${(((placement.bill_rate - placement.pay_rate) / placement.bill_rate) * 100).toFixed(1)}%` : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Discount & Fixed Bill Rate — always show, compute from discount_info if available */}
                    {(() => {
                      const discPct = placement.discount_info?.discount_pct || 0;
                      const discAmtHr = placement.discount_info?.discount_amount_per_hr || (placement.bill_rate * discPct / 100);
                      const fdr = placement.discount_info?.fixed_bill_rate || placement.fixed_bill_rate || (placement.bill_rate - discAmtHr);
                      return (
                        <div className="mt-3 grid grid-cols-3 gap-3" data-testid="discount-display">
                          <div className="text-center p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs text-amber-600">Discount</p>
                            <p className="text-base font-bold text-amber-800">{discPct}%</p>
                          </div>
                          <div className="text-center p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs text-amber-600">Discount Amount</p>
                            <p className="text-base font-bold text-amber-800">${discAmtHr.toFixed(2)}/hr</p>
                          </div>
                          <div className="text-center p-2 bg-emerald-50 rounded border border-emerald-200">
                            <p className="text-xs text-emerald-600">Fixed Bill Rate</p>
                            <p className="text-base font-bold text-emerald-800" data-testid="detail-fixed-bill-rate">${typeof fdr === 'number' ? fdr.toFixed(2) : fdr}/hr</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Margin */}
                    <div className="mt-3 p-2 bg-emerald-50 rounded-lg border border-emerald-200 flex justify-between items-center">
                      <span className="text-sm font-semibold text-emerald-800">Margin (incentives calculated on this)</span>
                      <span className="text-base font-bold text-emerald-900" data-testid="detail-margin">${placement.margin?.toFixed(2)}/hr</span>
                    </div>

                    {/* External Support */}
                    {placement.external_support && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="external-support-display">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">External Support</p>
                        <div className="grid grid-cols-3 gap-3">
                          {placement.external_support_amount > 0 && (
                            <div><p className="text-xs text-blue-500">Amount</p><p className="font-semibold text-blue-900">${placement.external_support_amount}</p></div>
                          )}
                          {placement.external_support_name && (
                            <div><p className="text-xs text-blue-500">Support Name</p><p className="font-semibold text-blue-900">{placement.external_support_name}</p></div>
                          )}
                          {placement.external_support_portal && (
                            <div><p className="text-xs text-blue-500">Job Portal</p><p className="font-semibold text-blue-900">{placement.external_support_portal}</p></div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dates & Employee Status */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {placement.start_date && (
                      <div><p className="text-sm text-slate-500">Start Date</p><p className="font-medium">{new Date(placement.start_date).toLocaleDateString()}</p></div>
                    )}
                    {placement.project_end_date && (
                      <div><p className="text-sm text-slate-500">Project End Date</p><p className="font-medium">{new Date(placement.project_end_date).toLocaleDateString()}</p></div>
                    )}
                    {placement.placement_month && placement.placement_year && (
                      <div><p className="text-sm text-slate-500">Placement Period</p><p className="font-medium">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][placement.placement_month - 1]} {placement.placement_year}</p></div>
                    )}
                    {placement.effective_bill_rate && placement.effective_bill_rate !== placement.bill_rate && (
                      <div><p className="text-sm text-slate-500">Effective Bill Rate</p><p className="font-medium text-amber-600">${placement.effective_bill_rate}/hr</p></div>
                    )}
                  </div>

                  {/* Employee Working Status */}
                  {placement.start_date && (
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="employee-status-section">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase">Employee Working Status</p>
                          <p className={`text-sm font-bold mt-1 ${
                            placement.employee_status === 'still_working' ? 'text-emerald-700' :
                            placement.employee_status === 'backout' ? 'text-red-700' :
                            placement.employee_status === 'performance_issue' ? 'text-orange-700' :
                            placement.employee_status === 'project_on_hold' ? 'text-amber-700' :
                            placement.employee_status === 'left' ? 'text-red-800' : 'text-slate-700'
                          }`} data-testid="employee-status-value">
                            {(placement.employee_status || 'still_working').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          {placement.employee_status_updated_by_name && (
                            <p className="text-xs text-slate-400 mt-0.5">Updated by {placement.employee_status_updated_by_name} — {new Date(placement.employee_status_updated_at).toLocaleString()}</p>
                          )}
                        </div>
                        {(isAdmin || isModuleManager || placement.created_by === user?.id) && (
                          <Select
                            value={placement.employee_status || 'still_working'}
                            onValueChange={async (val) => {
                              try {
                                await axios.put(`${API_URL}/api/placements/${id}/employee-status`, { employee_status: val }, { withCredentials: true });
                                toast.success('Employee status updated');
                                fetchPlacement();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
                            }}
                          >
                            <SelectTrigger className="w-48" data-testid="employee-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="still_working">Still Working</SelectItem>
                              <SelectItem value="backout">Backout</SelectItem>
                              <SelectItem value="performance_issue">Performance Issue</SelectItem>
                              <SelectItem value="project_on_hold">Project on Hold</SelectItem>
                              <SelectItem value="left">Left</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      {placement.start_date && (() => {
                        if (placement.sixty_day_completed === 'yes') {
                          return <p className="text-xs text-emerald-600 mt-2">60-day completion confirmed by recruiter</p>;
                        }
                        const days = Math.floor((Date.now() - new Date(placement.start_date).getTime()) / (1000 * 60 * 60 * 24));
                        return days < 60 ? (
                          <p className="text-xs text-amber-600 mt-2">Min. 2 months required for incentive approval — {60 - days} days remaining</p>
                        ) : (
                          <p className="text-xs text-emerald-600 mt-2">2-month minimum completed ({days} days)</p>
                        );
                      })()}
                    </div>
                  )}

                  {/* RTR File */}
                  {placement.rtr_filename && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between" data-testid="rtr-display">
                      <span className="text-sm text-blue-700 font-medium">{isRecruitingModule ? 'Candidate RTR' : 'Vendor RTR'} attached</span>
                      <a href={`${API_URL}/api/uploads/rtr/${placement.rtr_filename}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline">Download PDF</a>
                    </div>
                  )}

                  {/* Split Share Info (visible to lead/manager/admin) */}
                  {placement.split_share && (isModuleLead || isModuleManager || isSuperadmin || isCeo) && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="split-share-visible">
                      <p className="text-sm text-indigo-700 font-medium">
                        Recruiter shared {placement.split_share.percentage}% with {placement.split_share.user_name}
                      </p>
                    </div>
                  )}

                  {/* Split Partner notification — when the current user IS the split recipient */}
                  {placement.split_share && placement.split_share.user_id === user?.id && placement.created_by !== user?.id && (
                    <div className="p-4 bg-violet-50 rounded-lg border border-violet-300" data-testid="split-recipient-notice">
                      <p className="text-sm font-semibold text-violet-800">You were added as Split Partner</p>
                      <p className="text-sm text-violet-600 mt-1">
                        {placement.created_by_name} shared <strong>{placement.split_share.percentage}%</strong> of their incentive with you on this placement.
                      </p>
                      <p className="text-sm font-bold text-violet-800 mt-2">
                        Your Share: {formatCurrency(
                          placement.my_projected_payout
                          ?? placement.my_split_amount
                          ?? Math.round(
                            (placement.incentives?.total_direct
                              || placement.incentives?.sales_recruiter
                              || placement.incentives?.recruiter
                              || 0) * (placement.split_share.percentage / 100)
                          )
                        )}
                      </p>
                      {placement.start_date && <p className="text-xs text-violet-400 mt-1">Placement date: {new Date(placement.start_date).toLocaleDateString()}</p>}
                    </div>
                  )}

                  {/* Sourcing Recruiter notification — when the current user IS the sourcing support */}
                  {placement.sourcing_support === user?.id && placement.created_by !== user?.id && (
                    <div className="p-4 bg-teal-50 rounded-lg border border-teal-300" data-testid="sourcing-recipient-notice">
                      <p className="text-sm font-semibold text-teal-800">You were added as Sourcing Recruiter</p>
                      <p className="text-sm text-teal-600 mt-1">
                        {placement.created_by_name} added you as sourcing support on this placement.
                      </p>
                      {placement.status === 'approved' && (placement.incentives?.sourcing_recruiter || 0) > 0 && (
                        <p className="text-sm font-bold text-teal-800 mt-2">
                          Your Sourcing Share: {formatCurrency(placement.overrides?.sourcing_recruiter_amount ?? placement.incentives?.sourcing_recruiter)}
                        </p>
                      )}
                      {placement.start_date && <p className="text-xs text-teal-400 mt-1">Placement date: {new Date(placement.start_date).toLocaleDateString()}</p>}
                    </div>
                  )}

                  {/* Pending New Vendor / New Client / New MSP banner — shown until SA final-approves */}
                  {(placement.is_new_vendor_pending || placement.is_new_client_pending || placement.is_new_msp_pending) && placement.status !== 'approved' && placement.status !== 'sa_approved' && (
                    <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="new-entity-pending-banner">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-700 font-bold">⚠</span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-800">
                            {placement.is_new_vendor_pending && `New Vendor pending review: "${placement.vendor}"`}
                            {placement.is_new_client_pending && (placement.is_new_vendor_pending ? ' · ' : '') + `New Client pending review: "${placement.client}"`}
                            {placement.is_new_msp_pending && `New MSP pending review: "${placement.vendor}"`}
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            {(isAdmin) ? 'You can edit the spelling using the "Edit" button above. On final approval, this name will be auto-added to the master list.' : 'Awaiting Super Admin review. Once approved, this name will be added to the master list.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* New Technology with Admin Approval */}
                  {placement.is_new_technology && (
                    <div className={`p-3 rounded-lg border ${placement.new_tech_admin_approved === true ? 'bg-emerald-50 border-emerald-200' : placement.new_tech_admin_approved === false ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-testid="new-tech-section">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">New Technology — 1.2x multiplier {placement.new_tech_admin_approved === true ? '(Admin Approved)' : placement.new_tech_admin_approved === false ? '(Admin Rejected — 1.2x removed)' : '(Pending Admin Approval)'}</p>
                          {placement.new_technology_name && <p className="text-sm text-blue-700 mt-1">Technology: {placement.new_technology_name}</p>}
                          {placement.new_tech_approved_by_name && (
                            <p className="text-xs text-slate-500 mt-1">Reviewed by {placement.new_tech_approved_by_name} — {new Date(placement.new_tech_approved_at).toLocaleString()}</p>
                          )}
                        </div>
                        {isAdmin && placement.new_tech_admin_approved !== true && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-new-tech-btn"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API_URL}/api/placements/${id}/approve-new-tech`, { approved: true }, { withCredentials: true });
                                  toast.success('New technology approved — 1.2x multiplier confirmed');
                                  fetchPlacement();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}>Approve</Button>
                            <Button size="sm" variant="destructive" data-testid="reject-new-tech-btn"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API_URL}/api/placements/${id}/approve-new-tech`, { approved: false }, { withCredentials: true });
                                  toast.success('New technology rejected — 1.2x multiplier removed');
                                  fetchPlacement();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}>Reject</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cross-Team Split */}
                  {placement.is_cross_team && placement.cross_team_split && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200" data-testid="cross-team-display">
                      <p className="text-sm font-semibold text-purple-700">Cross-Team Placement (70/30 Split)</p>
                      <p className="text-sm text-purple-600 mt-1">30% to: {placement.cross_team_split.secondary_team}</p>
                    </div>
                  )}

                  {/* Additional Bonuses with Stage Info */}
                  {placement.additional_bonuses && placement.additional_bonuses.length > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="additional-bonuses-display">
                      <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">
                        {isAdmin ? 'Stage Bonuses (Paid to Creator)' : 'Your Vendor/Client Bonus'}
                      </p>
                      {placement.additional_bonuses.map((bonus, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm py-1.5 border-b border-emerald-100 last:border-0">
                          <div>
                            <span className="text-emerald-700 font-medium">{bonus.description}</span>
                            {bonus.stage && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 bg-emerald-200 text-emerald-800 rounded">
                                Placement {bonus.stage} of 6
                              </span>
                            )}
                          </div>
                          <span className="font-semibold text-emerald-800">{formatCurrency(bonus.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 60-Day Status Banner */}
                  {isRecruitingModule && ['submitted', 'lead_approved'].includes(placement.status) && sixtyDayStatus && (
                    <div className={`p-4 rounded-lg border ${sixtyDayStatus.can_add_completion ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center gap-2">
                        <Clock size={20} className={sixtyDayStatus.can_add_completion ? 'text-emerald-600' : 'text-amber-600'} />
                        {sixtyDayStatus.can_add_completion ? (
                          <p className="text-sm font-medium text-emerald-700">60-day period complete.{['pending_manager_review', 'lead_approved', 'pending_manager'].includes(placement.status) ? ' Manager can now approve or backout.' : ' Ready for completion data.'}</p>
                        ) : (
                          <p className="text-sm font-medium text-amber-700">
                            {sixtyDayStatus.days_remaining} days remaining ({sixtyDayStatus.days_elapsed}/60 days elapsed)
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Incentive Breakdown — visible to Leads, Managers, Superadmin, CEO */}
          {(isModuleLead || isModuleManager || isSuperadmin || isCeo) && (
            <Card className="border-slate-200" data-testid="incentive-breakdown-card">
              <CardHeader className="!flex-row !items-center !justify-between !space-y-0 gap-2">
                <CardTitle className="text-lg font-['Outfit']">Incentive Breakdown</CardTitle>
                <div className="flex items-center gap-2 shrink-0">
                  {isAdmin && !isEditingSplits && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowRateInfoPopup(true)} data-testid="edit-splits-btn" className="text-xs">
                      <PencilSimple size={14} className="mr-1" /> Override Values
                    </Button>
                  )}
                  {isEditingSplits && (
                    <>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setIsEditingSplits(false)} data-testid="cancel-splits-btn" className="text-xs">Cancel</Button>
                      <Button type="button" size="sm" onClick={handleSaveSplits} disabled={savingSplits} data-testid="save-splits-btn" className="text-xs bg-emerald-600 hover:bg-emerald-700">
                        {savingSplits ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <FloppyDisk size={14} className="mr-1" />} Save
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleSensitiveAmounts}
                    data-testid="toggle-incentive-amounts-btn"
                    aria-pressed={showSensitiveData}
                    className="text-xs"
                  >
                    {showSensitiveData ? <EyeSlash size={16} className="mr-1" /> : <Eye size={16} className="mr-1" />}
                    {showSensitiveData ? 'Hide amounts' : 'Show amounts'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total - only visible to SA/CEO */}
                  {isAdmin && (
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">Total Placement Incentive</span>
                    <span className="font-bold" data-testid="incentive-total-amount">
                      {fmtSensitive(placement.overrides?.total_placement || placement.incentives?.total_placement)}
                    </span>
                  </div>
                  )}
                  
                  {/* Split partner share — priority over lead/manager share */}
                  {isSplitRecipient && !isAdmin && (
                    <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg" data-testid="split-partner-own-share">
                      <p className="text-sm text-violet-600 mb-1">Recruiter Split Share</p>
                      <p className="text-xs text-violet-500 mb-2">
                        {placement.shared_by || placement.created_by_name} shared {placement.share_percentage ?? placement.split_share?.percentage}% of recruiter share with you
                      </p>
                      <p className="text-3xl font-bold text-violet-800" data-testid="split-partner-share-amount">
                        {fmtSensitive(partnerPayout)}
                      </p>
                    </div>
                  )}

                  {/* Additional split partner */}
                  {isAdditionalSplitRecipient && !isSplitRecipient && !isAdmin && (
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="additional-split-own-share">
                      <p className="text-sm text-indigo-600 mb-1">Your Additional Split</p>
                      <p className="text-3xl font-bold text-indigo-800">
                        {fmtSensitive(partnerPayout || placement.incentives?.my_additional_split || 0)}
                      </p>
                      {(placement.my_split_percentage || placement.additional_splits?.find(s => s.user_id === user?.id)?.percentage) && (
                        <p className="text-xs text-indigo-500 mt-1">
                          {placement.my_split_percentage || placement.additional_splits?.find(s => s.user_id === user?.id)?.percentage}% of total placement incentive
                        </p>
                      )}
                    </div>
                  )}

                  {/* Lead/Manager own share — only when NOT a split/additional partner on this placement */}
                  {(isModuleLead || isModuleManager) && !isAdmin && !isSplitRecipient && !isAdditionalSplitRecipient && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="lead-manager-own-share">
                      <p className="text-sm text-emerald-600 mb-1">Your {isModuleLead ? 'Lead' : 'Manager'} Share</p>
                      <p className="text-3xl font-bold text-emerald-700" data-testid="lead-manager-share-amount">
                        {fmtSensitive(placement.my_split_amount ?? placement.my_projected_payout ?? placement.incentives?.my_split ?? 0)}
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">Team member incentive amounts are hidden. Rate information is available above.</p>
                    </div>
                  )}

                  {/* Individual Splits with Names — full grid for SA/CEO only */}
                  {isRecruitingModule && isAdmin && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Role-wise Split</p>
                      <div className="grid grid-cols-3 gap-3">
                        {[{label: 'Recruiter', key: 'recruiter'}, {label: 'Lead', key: 'lead'}, {label: 'Manager', key: 'manager'}].map((item) => {
                          const hasAmount = placement.incentives?.[item.key] !== undefined;
                          const amount = placement.overrides?.[`${item.key}_amount`] ?? placement.incentives?.[item.key];
                          const isOverridden = placement.overrides?.[`${item.key}_amount`] !== undefined;
                          const assigneeName = placement.split_assignees?.[item.key];
                          return (
                            <div key={item.key} className={`p-3 rounded-lg ${isOverridden ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`split-${item.key}`}>
                              <p className="text-xs text-slate-400 uppercase">{item.label}</p>
                              <p className="text-sm font-semibold text-slate-800 mt-0.5">{assigneeName || 'Unassigned'}</p>
                              {isEditingSplits && splitEdits[item.key] !== undefined ? (
                                <Input
                                  type="number"
                                  value={splitEdits[item.key]}
                                  onChange={(e) => setSplitEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                                  className="mt-1 h-8 text-sm font-bold"
                                  data-testid={`split-input-${item.key}`}
                                />
                              ) : (
                                <p className={`font-bold text-lg mt-1 ${isOverridden ? 'text-amber-700' : 'text-slate-900'}`}>
                                  {!hasAmount ? <span className="text-slate-400 text-sm font-normal">Restricted</span>
                                    : fmtSensitive(amount)}
                                </p>
                              )}
                              {isOverridden && !isEditingSplits && <span className="text-xs text-amber-600">Override applied</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!isRecruitingModule && isAdmin && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Role-wise Split</p>
                      
                      {/* Total Direct (for existing categories) — includes vendor bonus */}
                      {placement.incentives?.total_direct > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="total-direct-display">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-xs text-blue-500 uppercase">Total Direct (to creator)</p>
                              <p className="text-sm text-blue-700 mt-0.5">{placement.split_assignees?.sales_recruiter || placement.created_by_name}</p>
                            </div>
                            <p className="font-bold text-lg text-blue-800">
                              {fmtSensitive(
                                (placement.incentives.total_direct || 0) + (placement.additional_bonuses || []).reduce((s, b) => s + (b.amount || 0), 0)
                              )}
                            </p>
                          </div>
                          {showSensitiveData && ((placement.additional_bonuses?.length > 0) || (placement.incentives.sourcing_recruiter > 0 && placement.sourcing_support)) && (
                            <div className="mt-2 pt-2 border-t border-blue-200 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-blue-500">
                              <span>Sales Recruiter: {formatCurrency(placement.incentives.sales_recruiter || 0)}</span>
                              {(placement.incentives.sourcing_recruiter || 0) > 0 && placement.sourcing_support && (
                                <span>+ Sourcing Recruiter: {formatCurrency(placement.incentives.sourcing_recruiter)}</span>
                              )}
                              {(placement.additional_bonuses || []).map((b, i) => (
                                <span key={i}>+ {b.type === 'new_client_stage' ? 'Client Bonus' : 'Vendor Bonus'}: {formatCurrency(b.amount)}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          {label: 'Sales Recruiter', key: 'sales_recruiter'},
                          {label: 'Sourcing Recruiter', key: 'sourcing_recruiter'},
                          {label: 'Sales Lead', key: 'sales_lead'},
                          {label: 'Sales Manager', key: 'sales_manager'},
                          {label: 'Sourcing Lead', key: 'sourcing_lead'},
                          {label: 'Sourcing Manager', key: 'sourcing_manager'},
                          {label: 'Recruiter', key: 'recruiter'},
                          {label: 'Lead', key: 'lead'},
                          {label: 'Manager', key: 'manager'},
                          {label: 'Your Additional Split', key: 'my_additional_split'},
                        ].filter(item => {
                          // SA/CEO see all relevant roles; others only see roles with data
                          if (isAdmin) {
                            // For bench sales, skip recruiting-only fields and vice versa
                            const recruitingKeys = ['recruiter', 'lead', 'manager', 'my_additional_split'];
                            const salesKeys = ['sales_recruiter', 'sourcing_recruiter', 'sales_lead', 'sales_manager', 'sourcing_lead', 'sourcing_manager'];
                            if (isRecruitingModule && salesKeys.includes(item.key)) return false;
                            if (!isRecruitingModule && recruitingKeys.includes(item.key)) return false;
                            // Always hide sourcing roles if no sourcing support was selected
                            const sourcingKeys = ['sourcing_recruiter', 'sourcing_lead', 'sourcing_manager'];
                            if (sourcingKeys.includes(item.key) && !placement.sourcing_support && !placement.split_assignees?.sourcing_recruiter) {
                              return false;
                            }
                            return true;
                          }
                          const amount = placement.incentives?.[item.key];
                          return amount !== undefined && amount !== null;
                        }).map((item) => {
                          const origAmount = placement.incentives?.[item.key] ?? 0;
                          const overriddenAmount = placement.overrides?.[`${item.key}_amount`];
                          const hasOverride = overriddenAmount !== undefined && overriddenAmount !== null;
                          const displayAmount = hasOverride ? overriddenAmount : origAmount;
                          const assigneeName = placement.split_assignees?.[item.key];
                          return (
                            <div key={item.key} className={`p-3 rounded-lg ${hasOverride ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`} data-testid={`split-${item.key}`}>
                              <p className="text-xs text-slate-400 uppercase">{item.label}</p>
                              <p className="text-sm font-semibold text-slate-800 mt-0.5">{assigneeName || 'Unassigned'}</p>
                              {isEditingSplits && splitEdits[item.key] !== undefined ? (
                                <Input
                                  type="number"
                                  value={splitEdits[item.key]}
                                  onChange={(e) => setSplitEdits(prev => ({ ...prev, [item.key]: e.target.value }))}
                                  className="mt-1 h-8 text-sm font-bold"
                                  data-testid={`split-input-${item.key}`}
                                />
                              ) : (
                                <>
                                  <p className={`font-bold text-lg mt-1 ${hasOverride ? 'text-amber-700' : 'text-slate-900'}`}>
                                    {fmtSensitive(displayAmount)}
                                  </p>
                                  {hasOverride && !isEditingSplits && showSensitiveData && (
                                    <p className="text-[10px] text-slate-400 line-through">{formatCurrency(origAmount)} original</p>
                                  )}
                                </>
                              )}
                              {hasOverride && !isEditingSplits && <span className="text-xs text-amber-600">Override applied</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Split Share (Recruiter's shared portion) — amount only for SA/CEO */}
                  {placement.split_share?.user_id && isAdmin && (
                    <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg" data-testid="split-share-detail">
                      <p className="text-xs font-semibold text-violet-500 uppercase mb-1">Recruiter Split Share</p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-semibold text-violet-800">{placement.split_share.user_name || 'Split Recipient'}</p>
                          <p className="text-xs text-violet-600">{placement.split_share.percentage}% of recruiter share</p>
                        </div>
                        <p className="font-bold text-violet-800">
                          {fmtSensitive(
                            (placement.incentives?.recruiter || placement.incentives?.sales_recruiter || placement.incentives?.total_direct || 0) * (placement.split_share.percentage / 100)
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Additional Splits — amounts only for SA/CEO */}
                  {placement.additional_splits?.length > 0 && isAdmin && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="additional-splits-detail">
                      <p className="text-xs font-semibold text-indigo-500 uppercase mb-2">Additional Splits</p>
                      <div className="space-y-2">
                        {placement.additional_splits.map((sp, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border border-indigo-100">
                            <div>
                              <p className="text-sm font-semibold text-indigo-800">{sp.user_name || 'Unknown'}</p>
                              <p className="text-xs text-indigo-600">{sp.percentage}% of total</p>
                            </div>
                            <p className="font-bold text-indigo-800" data-testid={`additional-split-amount-${idx}`}>
                              {fmtSensitive(
                                Math.round((placement.incentives?.total_placement || 0) * sp.percentage / 100)
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Incentive Change Log (Audit Trail for Overrides) */}
                  {placement.incentive_change_log?.length > 0 && (
                    <div className="space-y-2 pt-3 border-t border-slate-100" data-testid="incentive-change-log">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Override History</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {[...placement.incentive_change_log].reverse().map((log, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2.5 bg-amber-50/60 rounded-lg border border-amber-100" data-testid={`change-log-${idx}`}>
                            <div className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <PencilSimple size={12} className="text-amber-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700">
                                <span className="font-semibold">{log.changed_by}</span> changed <span className="font-medium text-amber-800">{log.field?.replace(/_/g, ' ')}</span>
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {fmtSensitive(log.old_value)} &rarr; {fmtSensitive(log.new_value)}
                              </p>
                              <p className="text-[10px] text-slate-400 mt-0.5">{new Date(log.changed_at).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Workflow Audit Trail */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Workflow Trail</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Created by</span><span className="font-medium">{placement.created_by_name || '—'}</span></div>
                      {placement.lead_approved_by_name && <div className="flex justify-between"><span className="text-slate-500">Lead reviewed by</span><span className="font-medium">{placement.lead_approved_by_name}</span></div>}
                      {placement.manager_approved_by_name && <div className="flex justify-between"><span className="text-slate-500">Manager confirmed by</span><span className="font-medium">{placement.manager_approved_by_name}</span></div>}
                      {placement.notes?.lead_review && <div className="flex justify-between"><span className="text-slate-500">Lead notes</span><span className="font-medium text-slate-700 text-right max-w-[60%]">{placement.notes.lead_review}</span></div>}
                      {placement.notes?.manager_review && <div className="flex justify-between"><span className="text-slate-500">Manager notes</span><span className="font-medium text-slate-700 text-right max-w-[60%]">{placement.notes.manager_review}</span></div>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}


          {/* Recruiter / split-partner payout card (any non-admin role when shared split) */}
          {((isRecruiter && !isModuleLead && !isModuleManager) || (isSplitRecipient && !isAdmin && !isModuleLead && !isModuleManager)) && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">
                  {placement.is_shared_split || isSplitRecipient ? 'Shared Split' : 'Your Projected Payout'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(placement.is_shared_split || isSplitRecipient) ? (
                  /* Shared split recipient view */
                  <div className="space-y-3">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-600 mb-1">{placement.shared_by || placement.created_by_name} shared {placement.share_percentage ?? placement.split_share?.percentage}% of their split with you</p>
                      <p className="text-3xl font-bold text-blue-700" data-testid="shared-split-amount">
                        {formatCurrency(partnerPayout)}
                      </p>
                      <p className="text-xs text-blue-500 mt-1">Your shared portion from this placement</p>
                    </div>
                  </div>
                ) : (
                  /* Creator view */
                  <>
                    <div className="p-6 bg-emerald-50 rounded-lg text-center">
                      <p className="text-sm text-emerald-600 mb-2">Estimated Earnings</p>
                      <p className="text-4xl font-bold text-emerald-700" data-testid="recruiter-payout">
                        {formatCurrency(placement.my_projected_payout || placement.incentives?.my_split)}
                      </p>
                    </div>
                    {/* Show split share info — only person + percentage, no amount */}
                    {placement.split_share_display && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="split-share-info">
                        <p className="text-sm font-medium text-amber-800">
                          Shared {placement.split_share_display.percentage}% with {placement.split_share_display.shared_with_name}
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">Amount deducted from your recruiter portion</p>
                      </div>
                    )}
                    {placement.split_names && Object.keys(placement.split_names).length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">Team Members in Split</p>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(placement.split_names).map(([role, name]) => (
                            <div key={role} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg" data-testid={`split-name-${role}`}>
                              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-blue-700">{name.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">{role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                                <p className="text-sm font-medium text-slate-800">{name}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes Section */}
          {placement.notes && Object.values(placement.notes).some(v => v) && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg font-['Outfit'] flex items-center gap-2"><NoteBlank size={20} /> Notes</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {placement.notes.submission && <div className="p-3 bg-blue-50 rounded-lg"><p className="text-xs font-semibold text-blue-600 uppercase">Submission</p><p className="text-sm text-blue-800 mt-1">{placement.notes.submission}</p></div>}
                {placement.notes.completion && <div className="p-3 bg-purple-50 rounded-lg"><p className="text-xs font-semibold text-purple-600 uppercase">Completion</p><p className="text-sm text-purple-800 mt-1">{placement.notes.completion}</p></div>}
                {placement.notes.lead_review && <div className="p-3 bg-teal-50 rounded-lg"><p className="text-xs font-semibold text-teal-600 uppercase">Lead Review</p><p className="text-sm text-teal-800 mt-1">{placement.notes.lead_review}</p>{placement.lead_approved_by_name && <p className="text-xs text-teal-500 mt-1">— {placement.lead_approved_by_name}</p>}</div>}
                {placement.notes.manager_review && <div className="p-3 bg-indigo-50 rounded-lg"><p className="text-xs font-semibold text-indigo-600 uppercase">Manager Review</p><p className="text-sm text-indigo-800 mt-1">{placement.notes.manager_review}</p>{placement.manager_approved_by_name && <p className="text-xs text-indigo-500 mt-1">— {placement.manager_approved_by_name}</p>}</div>}
              </CardContent>
            </Card>
          )}

          {/* Rejection reason (legacy) */}
          {placement.status === 'rejected' && placement.rejection_reason && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader><CardTitle className="text-lg font-['Outfit'] text-red-700">Rejection Reason</CardTitle></CardHeader>
              <CardContent>
                <p className="text-red-700">{placement.rejection_reason}</p>
                {placement.rejected_stage && <p className="text-sm text-red-500 mt-2">Rejected at: {placement.rejected_stage.replace(/_/g, ' ')}</p>}
              </CardContent>
            </Card>
          )}

          {/* Backout details — covers both recruiter backouts and approver rejections */}
          {placement.status === 'backed_out' && (placement.backout_reason || placement.backout_note) && (
            <Card className="border-rose-200 bg-rose-50" data-testid="backout-info-card">
              <CardHeader><CardTitle className="text-lg font-['Outfit'] text-rose-700">Backout Details</CardTitle></CardHeader>
              <CardContent>
                <p className="text-rose-700">{placement.backout_reason || placement.backout_note}</p>
                {placement.rejected_stage && <p className="text-sm text-rose-500 mt-2">Rejected at: {placement.rejected_stage.replace(/_/g, ' ')}</p>}
                {placement.backed_out_by_name && <p className="text-sm text-rose-500 mt-2">By: {placement.backed_out_by_name}</p>}
                {placement.backed_out_at && <p className="text-xs text-rose-400 mt-1">{new Date(placement.backed_out_at).toLocaleString()}</p>}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar — Actions + Timeline */}
        <div className="space-y-6">
          {/* Timeline */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Timeline</CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Approval workflow history — tracks every status change from creation to final decision.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-slate-400" /><div><p className="text-sm font-medium">Created</p><p className="text-xs text-slate-500">{new Date(placement.created_at).toLocaleString()} by {placement.created_by_name}</p></div></div>
                {placement.submitted_at && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-blue-400" /><div><p className="text-sm font-medium">Submitted</p><p className="text-xs text-slate-500">{new Date(placement.submitted_at).toLocaleString()}</p></div></div>}
                {isRecruitingModule && placement.start_date && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-purple-400" /><div><p className="text-sm font-medium">Start Date</p><p className="text-xs text-slate-500">{new Date(placement.start_date).toLocaleDateString()}</p></div></div>}
                {placement.completion_date && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-purple-400" /><div><p className="text-sm font-medium">Completion Added</p><p className="text-xs text-slate-500">{new Date(placement.completion_date).toLocaleString()}</p></div></div>}
                {placement.lead_approved_at && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-teal-400" /><div><p className="text-sm font-medium">Lead Approved</p><p className="text-xs text-slate-500">{new Date(placement.lead_approved_at).toLocaleString()} by {placement.lead_approved_by_name}</p></div></div>}
                {!isRecruitingModule && placement.reviewed_at && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-amber-400" /><div><p className="text-sm font-medium">Review Started</p><p className="text-xs text-slate-500">{new Date(placement.reviewed_at).toLocaleString()}</p></div></div>}
                {placement.approved_at && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-emerald-400" /><div><p className="text-sm font-medium">{isRecruitingModule ? 'Incentive Confirmed' : 'Approved'}</p><p className="text-xs text-slate-500">{new Date(placement.approved_at).toLocaleString()}</p></div></div>}
                {placement.rejected_at && <div className="flex gap-3"><div className="w-2 h-2 mt-2 rounded-full bg-red-400" /><div><p className="text-sm font-medium">Rejected</p><p className="text-xs text-slate-500">{new Date(placement.rejected_at).toLocaleString()}</p></div></div>}
              </div>
            </CardContent>
          </Card>

          {/* 60-Day Completion — Recruiter fills this */}
          {placement?.created_by === user?.id && !['approved', 'rejected', 'backed_out'].includes(placement?.status) && (
            <Card className="border-slate-200" data-testid="sixty-day-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">60-Day Completion</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">Update the candidate's 60-day completion status. If "Yes", the approval flow bypasses the 60-day wait. Add notes for context.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label>Did the candidate complete 60 days? *</Label>
                  <Select
                    value={placement.sixty_day_completed || ''}
                    onValueChange={async (v) => {
                      try {
                        await axios.put(`${API_URL}/api/placements/${id}/sixty-day-completion`, { sixty_day_completed: v }, { withCredentials: true });
                        toast.success('Updated'); fetchPlacement();
                      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
                    }}
                  >
                    <SelectTrigger data-testid="sixty-day-dropdown"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes - Completed 60 days</SelectItem>
                      <SelectItem value="no">No - Not yet completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes / Feedback</Label>
                  <Textarea
                    defaultValue={placement.sixty_day_notes || ''}
                    onBlur={async (e) => {
                      if (e.target.value !== (placement.sixty_day_notes || '')) {
                        try {
                          await axios.put(`${API_URL}/api/placements/${id}/sixty-day-completion`, { sixty_day_notes: e.target.value }, { withCredentials: true });
                          toast.success('Notes saved');
                        } catch (err) { toast.error('Failed to save notes'); }
                      }
                    }}
                    placeholder="Any feedback or notes about the 60-day period..."
                    rows={3}
                    data-testid="sixty-day-notes"
                  />
                </div>
                {placement.sixty_day_completed && (
                  <div className={`p-2 rounded text-sm ${placement.sixty_day_completed === 'yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {placement.sixty_day_completed === 'yes' ? 'Candidate has completed 60 days. Lead can now review.' : 'Candidate has not completed 60 days yet.'}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* 60-Day status display for Lead/Manager */}
          {placement?.created_by !== user?.id && placement.sixty_day_completed && (
            <Card className={`border-slate-200 ${placement.sixty_day_completed === 'yes' ? 'bg-emerald-50/50' : 'bg-amber-50/50'}`}>
              <CardContent className="p-4">
                <p className="text-sm font-medium">{placement.sixty_day_completed === 'yes' ? '60 Days: Completed' : '60 Days: Not Completed'}</p>
                {placement.sixty_day_notes && <p className="text-xs text-slate-600 mt-1">Notes: {placement.sixty_day_notes}</p>}
              </CardContent>
            </Card>
          )}

          {/* Recruiter Backout */}
          {placement?.created_by === user?.id && !['approved', 'rejected', 'backed_out'].includes(placement?.status) && (
            <Card className="border-red-200">
              <CardContent className="p-4">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowRecruiterBackoutModal(true)}
                  data-testid="recruiter-backout-btn"
                >
                  Mark as Backout
                </Button>
                <p className="text-xs text-red-500 mt-2 text-center">This will move the placement to the Backouts section</p>
              </CardContent>
            </Card>
          )}

          {/* Actions — Approve/Review */}
          {showSalesReviewActions && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg font-['Outfit']">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {placement.status === 'submitted' && <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={handleStartReview} disabled={actionLoading} data-testid="start-review-btn">Start Review</Button>}
                {canManagerApprove && !isRecruitingModule && (
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading} data-testid="manager-approve-btn">
                    <CheckCircle size={16} className="mr-2" /> Approve (Send to Admin)
                  </Button>
                )}
                {canLeadStandIn && !isRecruitingModule && (
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading} data-testid="lead-stand-in-approve-btn">
                    <CheckCircle size={16} className="mr-2" /> Approve as Lead (No Manager)
                  </Button>
                )}
                {canApprove && <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading} data-testid="approve-btn"><CheckCircle size={16} className="mr-2" /> Final Approve</Button>}
                {canOverride && <Button variant="outline" className="w-full" onClick={() => setShowOverrideModal(true)} data-testid="override-btn"><PencilSimple size={16} className="mr-2" /> Override Values</Button>}
                {(canReview || canLeadStandIn) && (placement?.can_reject !== false) && <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowRejectModal(true)} data-testid="reject-btn"><XCircle size={16} className="mr-2" /> Reject</Button>}
              </CardContent>
            </Card>
          )}

          {/* Actions — Recruiting Module */}
          {isRecruitingModule && (canAddCompletion || canLeadApprove || canManagerApprove || canLeadStandIn || canRejectRecruiting || canManagerBackout) && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg font-['Outfit']">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {canAddCompletion && sixtyDayStatus?.can_add_completion && (
                  <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => setShowCompletionModal(true)} disabled={actionLoading} data-testid="add-completion-btn">
                    <CalendarBlank size={16} className="mr-2" /> Add Completion Data
                  </Button>
                )}
                {canAddCompletion && !sixtyDayStatus?.can_add_completion && sixtyDayStatus && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <Clock size={20} className="mx-auto text-amber-600 mb-1" />
                    <p className="text-sm text-amber-700 font-medium">{sixtyDayStatus.days_remaining} days remaining</p>
                  </div>
                )}
                {canLeadApprove && (
                  <>
                    <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => setShowLeadApproveModal(true)} disabled={actionLoading} data-testid="submit-to-manager-btn">
                      <PaperPlaneTilt size={16} className="mr-2" /> Submit to Manager
                    </Button>
                    <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowRejectModal(true)} data-testid="lead-reject-btn">
                      <XCircle size={16} className="mr-2" /> Reject
                    </Button>
                  </>
                )}
                {canLeadStandIn && (
                  <>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={actionLoading} data-testid="lead-stand-in-approve-btn">
                      <CheckCircle size={16} className="mr-2" /> Approve as Lead (No Manager)
                    </Button>
                    <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowRejectModal(true)} data-testid="lead-stand-in-reject-btn">
                      <XCircle size={16} className="mr-2" /> Reject
                    </Button>
                  </>
                )}
                {canManagerApprove && (
                  <>
                    {managerSixtyDayBlocked ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center" data-testid="manager-60day-wait">
                        <Clock size={20} className="mx-auto text-amber-600 mb-1" />
                        <p className="text-sm text-amber-700 font-medium">60-day waiting period</p>
                        <p className="text-xs text-amber-600 mt-1">{sixtyDayStatus?.days_remaining} days remaining before you can approve or backout</p>
                      </div>
                    ) : (
                      <>
                        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowManagerApproveModal(true)} disabled={actionLoading} data-testid="confirm-incentive-btn">
                          <CheckCircle size={16} className="mr-2" /> Confirm Incentive
                        </Button>
                        <Button variant="outline" className="w-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setShowBackoutModal(true)} disabled={actionLoading} data-testid="manager-backout-btn">
                          <XCircle size={16} className="mr-2" /> Backout
                        </Button>
                      </>
                    )}
                  </>
                )}
                {canRejectRecruiting && (
                  <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowRejectModal(true)} data-testid="lead-reject-btn">
                    <XCircle size={16} className="mr-2" /> Reject
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Override Modal (Sales) — managers may only edit their own share */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Override Incentive Values</DialogTitle>
            <DialogDescription>
              {isAdmin
                ? 'Manually adjust amounts (INR).'
                : 'You can only adjust your manager share. Team member incentive amounts are hidden.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {(isAdmin
              ? [
                  {label: 'Sales Recruiter', key: 'sales_recruiter_amount'},
                  {label: 'Sourcing Recruiter', key: 'sourcing_recruiter_amount'},
                  {label: 'Sales Lead', key: 'sales_lead_amount'},
                  {label: 'Sales Manager', key: 'sales_manager_amount'},
                  {label: 'Sourcing Lead', key: 'sourcing_lead_amount'},
                  {label: 'Sourcing Manager', key: 'sourcing_manager_amount'},
                ]
              : [{label: 'Sales Manager (Your Share)', key: 'sales_manager_amount'}]
            ).map(item => (
              <div key={item.key} className="space-y-2">
                <Label>{item.label} (₹)</Label>
                <Input type="number" value={overrideData[item.key]} onChange={(e) => setOverrideData({...overrideData, [item.key]: e.target.value})} placeholder="0" />
              </div>
            ))}
          </div>
          <div className="space-y-2"><Label>Override Reason *</Label><Textarea value={overrideData.override_reason} onChange={(e) => setOverrideData({...overrideData, override_reason: e.target.value})} placeholder="Explain why..." rows={3} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setShowOverrideModal(false)}>Cancel</Button><Button onClick={handleOverride} disabled={actionLoading}>Apply Override</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Placement</DialogTitle><DialogDescription>Provide a reason for rejection.</DialogDescription></DialogHeader>
          <div className="space-y-2 py-4"><Label>Rejection Reason *</Label><Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter reason..." rows={4} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button><Button variant="destructive" onClick={handleReject} disabled={actionLoading}>Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Modal (Recruiting) */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Completion Data</DialogTitle><DialogDescription>After 60 days. This sends to Lead for review.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>End Date (Optional)</Label><Input type="date" value={completionForm.end_date} onChange={(e) => setCompletionForm({...completionForm, end_date: e.target.value})} /></div>
            <div className="space-y-2"><Label>Completion Notes *</Label><Textarea value={completionForm.completion_notes} onChange={(e) => setCompletionForm({...completionForm, completion_notes: e.target.value})} placeholder="Performance details..." rows={4} data-testid="input-completion-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCompletionModal(false)}>Cancel</Button><Button className="bg-purple-600 hover:bg-purple-700" onClick={handleAddCompletion} disabled={actionLoading}>Submit for Lead Review</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Submit to Manager Modal */}
      <Dialog open={showLeadApproveModal} onOpenChange={setShowLeadApproveModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit to Manager</DialogTitle>
            <DialogDescription>Review and add notes before sending to Manager for final confirmation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Lead Review Notes *</Label>
              <Textarea value={leadForm.lead_notes} onChange={(e) => setLeadForm({...leadForm, lead_notes: e.target.value})} placeholder="Your review observations..." rows={3} data-testid="input-lead-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeadApproveModal(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleLeadApprove} disabled={actionLoading} data-testid="confirm-submit-to-manager">
              <PaperPlaneTilt size={16} className="mr-2" /> Submit to Manager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manager Confirm Modal — only manager share is visible/editable */}
      <Dialog open={showManagerApproveModal} onOpenChange={setShowManagerApproveModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Incentive</DialogTitle>
            <DialogDescription>
              Confirm this placement. You can adjust your manager share only — team member incentive amounts stay hidden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Your Manager Share (₹)</Label>
              <Input
                type="number"
                value={managerForm.manager_amount}
                onChange={(e) => setManagerForm({...managerForm, manager_amount: e.target.value})}
                data-testid="input-manager-amount"
              />
            </div>
            <p className="text-xs text-slate-500">Leave as-is to keep the calculated manager share. Rate information is shown on the placement details.</p>
            <div className="space-y-2">
              <Label>Manager Notes *</Label>
              <Textarea value={managerForm.manager_notes} onChange={(e) => setManagerForm({...managerForm, manager_notes: e.target.value})} placeholder="Final review notes..." rows={4} data-testid="input-manager-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManagerApproveModal(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleManagerApprove} disabled={actionLoading} data-testid="confirm-incentive-final">
              <CheckCircle size={16} className="mr-2" /> Confirm Incentive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backout Modal (Manager) */}
      <Dialog open={showBackoutModal} onOpenChange={setShowBackoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Backout Placement</DialogTitle>
            <DialogDescription>This will mark the placement as backed out. A note is mandatory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label>Backout Note *</Label>
            <Textarea value={backoutNote} onChange={(e) => setBackoutNote(e.target.value)} placeholder="Explain the reason for backout..." rows={4} data-testid="input-backout-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackoutModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleManagerBackout} disabled={actionLoading} data-testid="confirm-backout-btn">
              <XCircle size={16} className="mr-2" /> Confirm Backout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Recruiter Backout Modal */}
      <Dialog open={showRecruiterBackoutModal} onOpenChange={setShowRecruiterBackoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Placement as Backout</DialogTitle>
            <DialogDescription>This will move the placement to the Backouts section. Please provide a reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Backout *</Label>
              <Textarea
                value={recruiterBackoutReason}
                onChange={(e) => setRecruiterBackoutReason(e.target.value)}
                placeholder="Explain why this placement is being backed out..."
                rows={4}
                data-testid="recruiter-backout-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecruiterBackoutModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRecruiterBackout} disabled={actionLoading} data-testid="confirm-recruiter-backout">
              Confirm Backout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Information Popup before Override */}
      <Dialog open={showRateInfoPopup} onOpenChange={setShowRateInfoPopup}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Rate Information — {placement?.candidate_first_name} {placement?.candidate_last_name}</DialogTitle>
            <DialogDescription>Current placement rate details. Review before overriding incentive values.</DialogDescription>
          </DialogHeader>
          {placement && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase">Bill Rate</p>
                  <p className="text-lg font-bold text-slate-900">${placement.bill_rate}/hr</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase">Pay Rate</p>
                  <p className="text-lg font-bold text-slate-900">${placement.pay_rate}/hr</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 uppercase">Candidate %</p>
                  <p className="text-lg font-bold text-blue-800">
                    {placement.candidate_percentage || (placement.bill_rate > 0 ? ((placement.pay_rate / placement.bill_rate) * 100).toFixed(1) : 0)}%
                  </p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase">Company %</p>
                  <p className="text-lg font-bold text-emerald-800">
                    {placement.company_percentage || (placement.bill_rate > 0 ? (((placement.bill_rate - placement.pay_rate) / placement.bill_rate) * 100).toFixed(1) : 0)}%
                  </p>
                </div>
              </div>
              {(() => {
                const discPct = placement.discount_info?.discount_pct || 0;
                const discAmtHr = placement.discount_info?.discount_amount_per_hr || (placement.bill_rate * discPct / 100);
                const fdr = placement.discount_info?.fixed_bill_rate || placement.fixed_bill_rate || (placement.bill_rate - discAmtHr);
                return (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 uppercase">Discount</p>
                      <p className="text-lg font-bold text-amber-800">{discPct}%</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 uppercase">Discount Amt</p>
                      <p className="text-lg font-bold text-amber-800">${discAmtHr.toFixed(2)}/hr</p>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-600 uppercase">Fixed Bill Rate</p>
                      <p className="text-lg font-bold text-emerald-800">${typeof fdr === 'number' ? fdr.toFixed(2) : fdr}/hr</p>
                    </div>
                  </div>
                );
              })()}
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-emerald-800">Margin (incentives calculated on this)</span>
                <span className="text-lg font-bold text-emerald-900">${placement.margin?.toFixed(2)}/hr</span>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <Button variant="outline" onClick={() => setShowRateInfoPopup(false)}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setShowRateInfoPopup(false); startEditingSplits(); }} data-testid="proceed-override-btn">
                  Proceed to Override
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlacementDetailPage;
